import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Download, FileText, Mail } from "lucide-react";
import { API } from "@/content";

export default function MediaKit() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { email: email.trim(), source: "media_kit" });
      toast.success("You're on the list — your media kit download is starting.");
      window.open(`${API}/media-kit.pdf`, "_blank");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="media-kit-assets-section" className="bg-[#12281D] border-t border-[#D4AF37]/15">
      <div className="mx-auto max-w-6xl px-5 py-20 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="rounded-[2rem] border border-[#D4AF37]/30 bg-gradient-to-br from-[#1B3B2B] to-[#0F2018] p-8 sm:p-12 lg:p-14 grid lg:grid-cols-2 gap-10 items-center"
        >
          <div>
            <div className="w-12 h-12 rounded-2xl bg-[#D4AF37]/15 flex items-center justify-center mb-6">
              <FileText size={22} className="text-[#D4AF37]" />
            </div>
            <p className="overline-gold mb-3">Press &amp; Partners</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#FBF9F1] leading-tight">
              Take the story <span className="gold-text italic">with you.</span>
            </h2>
            <p className="mt-4 text-sm sm:text-base text-[#D1DDD5] leading-relaxed max-w-md">
              The full media kit — bios, our story, services, track record, retreat details, and
              booking contacts — in one elegant PDF.
            </p>
            <a
              data-testid="download-media-kit-pdf-button"
              href={`${API}/media-kit.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold mt-8"
            >
              <Download size={16} />
              Download Media Kit (PDF)
            </a>
          </div>
          <form
            data-testid="media-kit-email-form"
            onSubmit={submit}
            className="rounded-3xl bg-[#F4EFE6] p-7 sm:p-9"
          >
            <p className="font-serif text-2xl text-[#1B3B2B]">Get it in your inbox</p>
            <p className="text-sm text-[#3A4D41] mt-2">
              Leave your email and we'll open the kit right away — plus keep you posted on future
              retreats.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <input
                data-testid="media-kit-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="input-brand"
              />
              <button
                data-testid="media-kit-email-submit"
                type="submit"
                disabled={loading}
                className="btn-gold w-full disabled:opacity-60"
              >
                <Mail size={16} />
                {loading ? "Sending..." : "Send & Download"}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
